/**
 * Websites Section — settings section for managing website tracking and form
 * capture automations.
 *
 * Shows only the 3 website-related automations (web_tracking_code,
 * web_form_creation, web_form_submission_processor) with setup and toggle
 * actions. Clicking a row opens the CRM object slideout for that automation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { QueryErrorState } from '@/lib/components';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { AutomationListItem } from './automation-list-item';

// ============================================================================
// Constants
// ============================================================================

const WEBSITE_SCRIPT_NAMES = [
  'web_tracking_code',
  'web_form_creation',
  'web_form_submission_processor',
] as const;

const WEBSITE_SCRIPT_NAME_SET = new Set<string>(WEBSITE_SCRIPT_NAMES);

const AUTOMATION_PROPERTIES = [
  'ahoy_display_name',
  'ahoy_enabled',
  'ahoy_summary',
  'ahoy_automation_name',
];

const FORM_PROPERTIES = ['ahoy_display_name'];

// ============================================================================
// WebsitesContent
// ============================================================================

function WebsitesContent() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const { openSlideout } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const canToggle = hasPermission(
    'ahoy_event_automation',
    'ahoy_property_updated'
  );
  const canCreate = hasPermission(
    'ahoy_event_automation',
    'ahoy_object_created'
  );

  const { schema } = usePropertySchema(orgId ?? 0, 'ahoy_event_automation');
  const summarySchema = schema?.['ahoy_summary'];

  const query = crmObjectListSentinelQuery(
    orgId ?? 0,
    'ahoy_event_automation',
    {
      properties: AUTOMATION_PROPERTIES,
    }
  );

  const listQuery = useQuery({
    ...query,
    enabled: !!orgId,
  });

  const {
    results: allAutomationResults,
    isLoading,
    error,
    refetch,
  } = useStoreBackedList(listQuery);

  const websiteAutomations = useMemo(() => {
    return allAutomationResults.filter(a => {
      const scriptName = a.properties?.['ahoy_automation_name']?.value;
      return (
        typeof scriptName === 'string' &&
        WEBSITE_SCRIPT_NAME_SET.has(scriptName)
      );
    });
  }, [allAutomationResults]);

  const setupMutation = useMutation({
    mutationFn: () => {
      if (!orgId) {
        return Promise.reject(new Error('Organization not available'));
      }
      return apiPost<{ slug: string; webhook_entity_id: string }>(
        `/api/orgs/${orgId}/websites/automations/setup/`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
      toast.success('Website automations created');
    },
    onError: err => {
      logger.error(
        'Failed to set up website automations',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't set up website automations. Try again.");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({
      entityId,
      enabled,
    }: {
      entityId: string;
      enabled: string;
    }) => {
      if (!orgId || !user) {
        return Promise.reject(new Error('Organization not available'));
      }
      const automation = websiteAutomations.find(a => a.entity_id === entityId);
      const startSeq = automation?.last_event_seq ?? 0;
      const builder = createEventBuilder({
        sourceId: String(user.id),
        startSeq,
      });
      builder.propertyUpdated(
        entityId,
        'ahoy_event_automation',
        'ahoy_enabled',
        enabled
      );
      const client = createEventsClient(orgId);
      return client.submitEvents(builder.build());
    },
    onError: err => {
      logger.error(
        'Failed to update automation',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't update the automation. Try again.");
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
      toast.success('Automation updated');
    },
  });

  const handleSetup = useCallback(() => {
    setupMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
  }, [setupMutation.mutate]);

  const handleToggle = useCallback(
    (entityId: string, enabled: string) => {
      toggleMutation.mutate({ entityId, enabled });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
    [toggleMutation.mutate]
  );

  const handleRowClick = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  if (isLoading) {
    return (
      <div
        role='status'
        aria-live='polite'
        aria-label='Loading website automations'
        className='space-y-3'
      >
        {[1, 2, 3].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        error={error}
        label="We couldn't load website automations"
        onRetry={refetch}
      />
    );
  }

  if (!orgId) {
    return null;
  }

  const hasWebsiteAutomations = websiteAutomations.length > 0;

  return hasWebsiteAutomations ? (
    <ButtonList aria-label='Website automations'>
      {websiteAutomations.map(automation => (
        <AutomationListItem
          key={automation.entity_id}
          automation={automation}
          summarySchema={summarySchema}
          orgId={orgId}
          onToggle={handleToggle}
          onRowClick={handleRowClick}
          isToggling={toggleMutation.isPending}
          canToggle={canToggle}
        />
      ))}
    </ButtonList>
  ) : (
    <div
      role='status'
      aria-live='polite'
      className='flex flex-col items-center justify-center py-12 text-center'
    >
      <p className='text-sm text-fg-tertiary mb-4'>
        Set up website tracking and form capture to start collecting visitor
        data.
      </p>
      <Button
        size='sm'
        color='primary'
        onClick={handleSetup}
        isDisabled={!canCreate || setupMutation.isPending}
        disabledTooltip='Only admins have permission to edit automations'
      >
        {setupMutation.isPending ? 'Setting up…' : 'Set up'}
      </Button>
    </div>
  );
}

// ============================================================================
// FormsContent
// ============================================================================

interface FormListItemProps {
  form: CrmObject;
  onRowClick: (entityId: string) => void;
}

function FormListItem({ form, onRowClick }: FormListItemProps) {
  const displayName =
    (form.properties?.['ahoy_display_name']?.value as string | undefined) ??
    'Untitled form';

  const handleAction = useCallback(() => {
    onRowClick(form.entity_id);
  }, [form.entity_id, onRowClick]);

  return (
    <ButtonList.Item
      onAction={handleAction}
      aria-label={`Open ${displayName} details`}
    >
      <span className='text-sm font-medium text-fg-primary'>{displayName}</span>
    </ButtonList.Item>
  );
}

function FormsContent() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { openSlideout } = useSlideoutStack();

  const query = crmObjectListSentinelQuery(orgId ?? 0, 'ahoy_web_form', {
    properties: FORM_PROPERTIES,
  });

  const listQuery = useQuery({
    ...query,
    enabled: !!orgId,
  });

  const {
    results: forms,
    isLoading,
    error,
    refetch,
  } = useStoreBackedList(listQuery);

  const handleFormRowClick = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  if (!orgId) {
    return null;
  }

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div
        role='status'
        aria-live='polite'
        aria-label='Loading web forms'
        className='space-y-3'
      >
        {[1, 2].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  } else if (error) {
    content = (
      <QueryErrorState
        error={error}
        label="We couldn't load the forms"
        onRetry={refetch}
      />
    );
  } else if (forms.length === 0) {
    content = (
      <div
        role='status'
        aria-live='polite'
        className='flex flex-col items-center justify-center py-12 text-center'
      >
        <p className='text-sm text-fg-tertiary'>No forms captured yet.</p>
      </div>
    );
  } else {
    content = (
      <ButtonList aria-label='Forms'>
        {forms.map(form => (
          <FormListItem
            key={form.entity_id}
            form={form}
            onRowClick={handleFormRowClick}
          />
        ))}
      </ButtonList>
    );
  }

  return (
    <div className='space-y-3'>
      <h3 className='text-caption text-fg-tertiary'>Forms</h3>
      {content}
    </div>
  );
}

// ============================================================================
// WebsitesSection
// ============================================================================

export function WebsitesSection() {
  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Websites</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Manage website tracking and form capture.
        </p>
      </div>
      <WebsitesContent />
      <FormsContent />
    </div>
  );
}
